import { api } from "../lib/api";

interface TwoFactorResponse {
	success: boolean;
	message?: string;
	error?: string;
	completedAt?: string;
	maskedPhoneNumber?: string;
}

export class TwoFactorService {
	async requestOTP(
		method?: string,
		phoneNumber?: string,
	): Promise<TwoFactorResponse> {
		try {
			const response = await api.post<TwoFactorResponse>(
				"/api/2fa/request-otp",
				{ method, phoneNumber },
			);
			return response.data;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { data?: { error?: string } };
				};
				if (axiosError.response?.data?.error) {
					throw new Error(axiosError.response.data.error);
				}
			}
			throw new Error("Failed to send verification code");
		}
	}

	async verifyOTP(
		otp: string,
		method?: string,
		phoneNumber?: string,
	): Promise<TwoFactorResponse> {
		try {
			const response = await api.post<TwoFactorResponse>(
				"/api/2fa/verify-otp",
				{ otp, method, phoneNumber },
			);
			return response.data;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { data?: { error?: string } };
				};
				if (axiosError.response?.data?.error) {
					throw new Error(axiosError.response.data.error);
				}
			}
			throw new Error("Invalid verification code");
		}
	}

	async complete2FA(phoneNumber?: string): Promise<TwoFactorResponse> {
		try {
			const response = await api.post<TwoFactorResponse>(
				"/api/2fa/complete-2fa",
				{ phoneNumber },
			);
			return response.data;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { data?: { error?: string } };
				};
				if (axiosError.response?.data?.error) {
					throw new Error(axiosError.response.data.error);
				}
			}
			throw new Error("Failed to complete 2FA");
		}
	}

	async checkUserNeeds2FA(): Promise<boolean> {
		try {
			const response = await api.get<{ needs_2fa: boolean }>(
				"/api/user/status",
			);
			return response.data.needs_2fa;
		} catch (error: unknown) {
			console.error("Failed to check 2FA status:", error);
			// Default to true (require 2FA) on error to be safe
			return true;
		}
	}

	async getUserStatus(): Promise<{
		needs_2fa: boolean;
		needs_onboarding: boolean;
		role: string;
		user_type: string | null;
		display_language: string;
	}> {
		const response = await api.get<{
			needs_2fa: boolean;
			needs_onboarding: boolean;
			role: string;
			user_type: string | null;
			display_language: string;
		}>("/api/user/status");
		return response.data;
	}
}

export const twoFactorService = new TwoFactorService();
